import types from '@src/store/types';
import { useKeycloakToken } from '../plugins/axios-keycloak';

const axios = useKeycloakToken();

const MAX_LABEL_LENGTH = 1000;

function buildQueryString(params) {
  if (!params) return '';
  if (params instanceof URLSearchParams) return params.toString();
  if (typeof params === 'string') return params;
  if (typeof params !== 'object') return String(params);

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([
    key, value 
  ]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined) searchParams.append(key, String(item));
      });
      return;
    }
    if (value !== undefined) searchParams.append(key, String(value));
  });
  return searchParams.toString();
}

function addQueryToUrl(url, params) {
  if (!url) return url;
  const queryString = buildQueryString(params);
  if (!queryString) return url;
  return url.includes('?') ? `${url}&${queryString}` : `${url}?${queryString}`;
}

function trimLabel(label, maxLength = MAX_LABEL_LENGTH) {
  if (!label || label.length <= maxLength) return label;
  return `${label.slice(0, maxLength)}...`;
}

function trackApiRequest({ method, url, params, duration, status, isError }) {
  if (typeof window === 'undefined' || !window._paq) return;

  const fullUrl = typeof url === 'string' ? addQueryToUrl(url, params) : 'unknown';
  const action = String(method || 'UNKNOWN').toUpperCase();
  const statusLabel = status ?? 'ERR';
  const durationMs = Number.isFinite(duration) ? Math.round(duration) : undefined;
  const parts = [ `${fullUrl}` ];
  if (durationMs !== undefined) parts.push(`duration=${durationMs}ms`);
  parts.push(`status=${statusLabel}`);
  const name = trimLabel(parts.join(' | '));
  const category = isError ? 'API_ERROR' : 'API';
  const event = [
    'trackEvent', category, action, name 
  ];

  window._paq.push(event);
}

/**
 *
 * @param {String} method request method
 * @param {String} url
 * @param {JSON} payload Object which will be send
 */
async function executeAxios(method, url, payload = undefined, config = undefined) {
  if (!method) throw new Error('Request must have a method');
  if (!url) throw new Error('Request must have an url');

  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    const startTime = performance?.now?.();
    try {
      const requestUrl = `${apiUrl}${url}`;
      const normalizedMethod = method.toLowerCase();
      const axiosMethod = axios[normalizedMethod];

      if (typeof axiosMethod !== 'function') {
        throw new Error(`Unsupported request method: ${method}`);
      }

      let request;
      if (normalizedMethod === 'get' || normalizedMethod === 'delete') {
        const requestConfig =
          payload !== undefined ? { ...(config || {}), params: payload } : config;
        request = axiosMethod(requestUrl, requestConfig);
      } else if (config !== undefined) {
        request = axiosMethod(requestUrl, payload, config);
      } else {
        request = axiosMethod(requestUrl, payload);
      }
      const response = await request;
      trackApiRequest({
        method: normalizedMethod,
        url,
        params:
          normalizedMethod === 'get' || normalizedMethod === 'delete'
            ? payload ?? config?.params
            : undefined,
        duration: Number.isFinite(startTime) ? performance.now() - startTime : undefined,
        status: response?.status,
        isError: false
      });
      resolve(response);
    } catch (error) {
      trackApiRequest({
        method,
        url,
        params: method === 'get' || method === 'delete' ? payload ?? config?.params : undefined,
        duration: Number.isFinite(startTime) ? performance.now() - startTime : undefined,
        status: error?.response?.status,
        isError: true
      });
      reject(error);
    }
  });
}

/**
 *
 * @param {String} url
 */
function executeGet(url, config) {
  return executeAxios('get', url, undefined, config);
}

/**
 *
 * @param {String} url
 * @param {JSON} payload
 */
function executePut(url, payload, config) {
  return executeAxios('put', url, payload, config);
}

/**
 *
 * @param {String} url
 * @param {JSON} payload
 */
function executeDelete(url, config) {
  return executeAxios('delete', url, undefined, config);
}

/**
 *
 * @param {String} url
 * @param {JSON} payload
 */
function executePost(url, payload, config) {
  return executeAxios('post', url, payload, config);
}

/**
 *
 * @param {String} url
 * @param {JSON} payload
 */
function executePatch(url, payload, config) {
  return executeAxios('patch', url, payload, config);
}

const viewContentMutations = types.mutations.viewContent;

function authWrapper({ commit }, promise) {
  return new Promise((resolve, reject) => {
    promise
      .then((result) => {
        resolve(result);
      })
      .catch((error) => {
        if (error && error.response?.status === 403) {
          commit(
            `${viewContentMutations.SHOW_SNACKBAR_ERROR}`,
            'Sie sind nicht berechtigt diese Aktion auszuführen'
          );
          resolve();
          return;
        }
        reject(error);
      });
  });
}

export {
  executeGet as get,
  executePut as put,
  executePost as post,
  executePatch as patch,
  executeDelete as remove,
  authWrapper
};
